import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Link,
  Alert,
  AlertTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardContent,
  Card
} from '@mui/material';
import {
  Close,
  Business,
  LocationOn,
  Language,
  Assessment,
  Warning,
  CheckCircle,
  OpenInNew,
  AttachMoney,
  ExpandMore,
  Info,
  TrendingUp,
  PieChart,
  AccountBalance,
  Security,
  Shield,
  Policy
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { closeDetailModal } from '../../store/slices/vendorSlice';
// TODO: Implement OFAC slice
// import { selectOFACScreeningResult } from '../../store/slices/ofacSlice';
import { fraudUtils } from '../../services/api';
import RiskScoreBadge from './RiskScoreBadge';
import FraudIndicatorChips from './FraudIndicatorChips';
import VendorLocationMap from './VendorLocationMap';
import CompetitionHealthIndicator from './CompetitionHealthIndicator';
import ComplianceRiskPanel from './ComplianceRiskPanel';
// TODO: Fix OFAC components implementation
// import OFACScreeningPanel from './OFACScreeningPanel';
// import OFACStatusBadge from './OFACStatusBadge';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ height: '100%' }}>
      {value === index && <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>{children}</Box>}
    </div>
  );
};

const VendorDetailModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination and sorting state for SAM awards
  const [samPage, setSamPage] = useState(0);
  const [samRowsPerPage, setSamRowsPerPage] = useState(10);
  const [samOrderBy, setSamOrderBy] = useState<string>('date');
  const [samOrder, setSamOrder] = useState<'asc' | 'desc'>('desc');
  
  // Pagination and sorting state for USAspending awards
  const [usaPage, setUsaPage] = useState(0);
  const [usaRowsPerPage, setUsaRowsPerPage] = useState(10);
  const [usaOrderBy, setUsaOrderBy] = useState<string>('date');
  const [usaOrder, setUsaOrder] = useState<'asc' | 'desc'>('desc');
  
  // Sorting helper function
  const createSortComparator = (orderBy: string, order: 'asc' | 'desc') => {
    return (a: any, b: any) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      // Handle nested properties
      if (orderBy === 'amount') {
        aValue = a.award_amount || a.amount || 0;
        bValue = b.award_amount || b.amount || 0;
      } else if (orderBy === 'date') {
        aValue = a.award_date || a.date || '';
        bValue = b.award_date || b.date || '';
      } else if (orderBy === 'agency') {
        aValue = a.awarding_agency_name || a.agency || '';
        bValue = b.awarding_agency_name || b.agency || '';
      }
      
      // Convert to comparable values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Compare values
      if (bValue < aValue) {
        return order === 'desc' ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === 'desc' ? 1 : -1;
      }
      return 0;
    };
  };
  
  const {
    detailModalOpen,
    selectedVendor,
    vendorDetails,
    samAwards,
    usaspendingAwards,
    samTotals,
    usaspendingTotals,
    vendorSummary,
    loading
  } = useAppSelector(state => state.vendor);

  const handleClose = () => {
    dispatch(closeDetailModal());
    setTabValue(0);
  };

  if (!selectedVendor) {
    return null;
  }

  const formatCurrency = (amount: number) => {
    return fraudUtils.formatCurrency(amount);
  };

  const formatDate = (dateString: string) => {
    return fraudUtils.formatDate(dateString);
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel?.toUpperCase()) {
      case 'HIGH':
      case 'CRITICAL':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'success';
      default:
        return 'info';
    }
  };

  // Helper function to check if award has set-aside violations
  const hasSetAsideViolation = (awardId: string, setAside: string) => {
    const mappedRisk = mapRiskAnalysis(vendorSummary?.risk_analysis, vendorSummary?.fraud_analysis);
    if (!mappedRisk?.risk_indicators) return false;
    
    return mappedRisk.risk_indicators.some((indicator: any) => 
      indicator.type === 'set_aside_violation' &&
      indicator.evidence &&
      indicator.evidence.award_id === awardId &&
      indicator.evidence.set_aside === setAside
    );
  };

  // Helper function to get set-aside violation details
  const getSetAsideViolation = (awardId: string, setAside: string) => {
    const mappedRisk = mapRiskAnalysis(vendorSummary?.risk_analysis, vendorSummary?.fraud_analysis);
    if (!mappedRisk?.risk_indicators) return null;
    
    return mappedRisk.risk_indicators.find((indicator: any) => 
      indicator.type === 'set_aside_violation' &&
      indicator.evidence &&
      indicator.evidence.award_id === awardId &&
      indicator.evidence.set_aside === setAside
    );
  };

  // Helper function to map backend risk analysis to frontend format
  const mapRiskAnalysis = (riskAnalysis: any, fraudAnalysis: any) => {
    console.log('mapRiskAnalysis - riskAnalysis:', riskAnalysis);
    console.log('mapRiskAnalysis - fraudAnalysis:', fraudAnalysis);
    
    if (!riskAnalysis && !fraudAnalysis) return null;
    
    // Check if we have the new unified structure
    if (riskAnalysis?.risk_score !== undefined && riskAnalysis?.risk_indicators) {
      return {
        risk_score: riskAnalysis.risk_score,
        risk_level: riskAnalysis.risk_level,
        risk_indicators: riskAnalysis.risk_indicators || [],
        fraud_probability: riskAnalysis.fraud_probability || 0,
        investigation_priority: riskAnalysis.investigation_priority || riskAnalysis.risk_level,
        requires_investigation: riskAnalysis.requires_investigation || false
      };
    }
    
    // Fallback to old mapping for backward compatibility
    return {
      risk_score: fraudAnalysis?.overall_risk_score || riskAnalysis?.total_risk_score || 0,
      risk_level: (fraudAnalysis?.risk_level || riskAnalysis?.risk_level || 'LOW') as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
      risk_indicators: fraudAnalysis?.fraud_indicators || [],
      fraud_probability: riskAnalysis?.fraud_risk_score || 0,
      investigation_priority: (fraudAnalysis?.risk_level || riskAnalysis?.risk_level || 'LOW') as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
      requires_investigation: (fraudAnalysis?.total_indicators || 0) > 0
    };
  };

  // Helper function to map competition data from combined_totals
  const mapCompetitionAnalysis = (combinedTotals: any) => {
    if (!combinedTotals) return null;
    
    const totalAwards = combinedTotals.award_count || 0;
    const soleSourceCount = combinedTotals.sole_source_count || 0;
    const fullCompetitionCount = combinedTotals.full_competition_count || 0;
    const awardsWithData = soleSourceCount + fullCompetitionCount;
    
    return {
      sole_source_count: soleSourceCount,
      sole_source_percentage: combinedTotals.sole_source_percentage || 0,
      full_competition_count: fullCompetitionCount,
      full_competition_percentage: combinedTotals.full_competition_percentage || 0,
      avg_competition_count: 0, // Not provided in backend response
      awards_with_data: awardsWithData,
      total_awards: totalAwards,
      competition_rate: (combinedTotals.full_competition_percentage || 0) / 100
    };
  };

  const LoadingSkeleton = () => (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="60%" height={40} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map(i => (
          <Grid item xs={12} md={6} key={i}>
            <Skeleton variant="rectangular" height={100} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Dialog
      open={detailModalOpen}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { 
          height: '95vh',
          maxHeight: '95vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              {vendorDetails?.legal_business_name || selectedVendor.legal_business_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              UEI: {selectedVendor.uei_sam}
            </Typography>
          </Box>
          {vendorDetails && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {vendorDetails.registration_status === 'Active' ? (
                <Chip
                  icon={<CheckCircle />}
                  label="Active Registration"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<Warning />}
                  label={vendorDetails.registration_status || 'Inactive'}
                  color="warning"
                  size="small"
                />
              )}
              {/* OFAC Status Badge - TODO: Implement */}
              {/* {selectedVendor && (
                <OFACStatusBadge
                  screeningResult={useAppSelector(state => selectOFACScreeningResult(state, selectedVendor.uei_sam))}
                  size="small"
                />
              )} */}
              {(() => {
                const mappedRisk = mapRiskAnalysis(vendorSummary?.risk_analysis, vendorSummary?.fraud_analysis);
                return mappedRisk ? (
                  <RiskScoreBadge
                    score={mappedRisk.risk_score}
                    level={mappedRisk.risk_level}
                    size="small"
                  />
                ) : null;
              })()}
            </Box>
          )}
        </Box>
        <IconButton onClick={handleClose} size="large">
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="General Information" icon={<Business />} iconPosition="start" />
          <Tab label={`SAM Awards (${samAwards?.length || 0})`} icon={<AccountBalance />} iconPosition="start" />
          <Tab label={`USAspending Awards (${usaspendingAwards?.length || 0})`} icon={<AttachMoney />} iconPosition="start" />
          <Tab label="Compliance & Risk" icon={<Shield />} iconPosition="start" />
          <Tab label="Summary & Risk Analysis" icon={<Assessment />} iconPosition="start" />
        </Tabs>
      </Box>

      <DialogContent 
        dividers 
        sx={{ 
          p: 0, 
          height: 'calc(95vh - 200px)',
          overflow: 'hidden'
        }}
      >
        {/* Tab 1: General Information */}
        <TabPanel value={tabValue} index={0}>
          {loading || !vendorDetails ? (
            <LoadingSkeleton />
          ) : (
            <Grid container spacing={3}>
              {/* First Row - Basic Info */}
              {/* Entity Registration - Left */}
              <Grid item xs={12} lg={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business /> Entity Registration
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="Legal Business Name"
                          secondary={vendorDetails.legal_business_name}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="UEI (SAM)"
                          secondary={
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                              {vendorDetails.uei_sam}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="CAGE Code"
                          secondary={vendorDetails.cage_code}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Registration Date"
                          secondary={formatDate(vendorDetails.registration_date)}
                        />
                      </ListItem>
                      {vendorDetails.expiration_date && (
                        <ListItem>
                          <ListItemText
                            primary="Expiration Date"
                            secondary={formatDate(vendorDetails.expiration_date)}
                          />
                        </ListItem>
                      )}
                      {vendorDetails.entity_url && (
                        <ListItem>
                          <ListItemText
                            primary="Website"
                            secondary={
                              <Link 
                                href={vendorDetails.entity_url.startsWith('http') ? vendorDetails.entity_url : `https://${vendorDetails.entity_url}`} 
                                target="_blank" 
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                {vendorDetails.entity_url} 
                                <OpenInNew sx={{ fontSize: 14 }} />
                              </Link>
                            }
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Business Types & Physical Address - Middle */}
              <Grid item xs={12} lg={4}>
                <Grid container spacing={3}>
                  {/* Business Types */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Business Types
                        </Typography>
                        {vendorDetails.business_types?.businessTypeList?.length ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {vendorDetails.business_types.businessTypeList.map((type: any, index: number) => (
                              <Chip
                                key={index}
                                label={type.businessTypeDesc}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No business type information available
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Physical Address */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn /> Physical Address
                        </Typography>
                        {vendorDetails.physical_address ? (
                          <Box>
                            <Typography variant="body2">
                              {vendorDetails.physical_address.addressLine1}
                            </Typography>
                            {vendorDetails.physical_address.addressLine2 && (
                              <Typography variant="body2">
                                {vendorDetails.physical_address.addressLine2}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {vendorDetails.physical_address.city}, {vendorDetails.physical_address.stateOrProvinceCode} {vendorDetails.physical_address.zipCode}
                              {vendorDetails.physical_address.zipCodePlus4 && `-${vendorDetails.physical_address.zipCodePlus4}`}
                            </Typography>
                            {vendorDetails.physical_address.countryCode && (
                              <Typography variant="body2" color="text.secondary">
                                {vendorDetails.physical_address.countryCode}
                              </Typography>
                            )}
                            {vendorDetails.congressional_district && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Congressional District:</strong> {vendorDetails.congressional_district}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No address information available
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>

              {/* NAICS Codes - Right */}
              <Grid item xs={12} lg={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      NAICS Codes
                    </Typography>
                    {vendorDetails.primary_naics && (
                      <Typography variant="body2" sx={{ mb: 2, p: 1, backgroundColor: 'primary.light', color: 'primary.contrastText', borderRadius: 1 }}>
                        <strong>Primary:</strong> {vendorDetails.primary_naics}
                      </Typography>
                    )}
                    {vendorDetails.naics_list?.length ? (
                      <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {vendorDetails.naics_list.map((naics: any, index: number) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                    {naics.naicsCode}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    {naics.naicsDescription}
                                  </Typography>
                                  {naics.sbaSmallBusiness === 'Y' && (
                                    <Chip label="SBA Small Business" size="small" color="success" />
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No NAICS information available
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Google Maps and Places Information - Full Width Below */}
              <Grid item xs={12}>
                <VendorLocationMap
                  vendorUei={selectedVendor.uei_sam}
                  vendorName={vendorDetails.legal_business_name}
                  address={vendorDetails.physical_address}
                />
              </Grid>
            </Grid>
          )}
        </TabPanel>

        {/* Tab 2: SAM Awards */}
        <TabPanel value={tabValue} index={1}>
          {samAwards && samAwards.length > 0 ? (
            <>
              {samTotals && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">
                    <strong>SAM Awards Summary:</strong> {samTotals.award_count} awards worth {formatCurrency(samTotals.total_amount)}
                  </Typography>
                  {samTotals.avg_award_amount && (
                    <Typography variant="body2">
                      Average award amount: {formatCurrency(samTotals.avg_award_amount)}
                    </Typography>
                  )}
                </Alert>
              )}
              <TableContainer component={Paper}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Award ID</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={samOrderBy === 'date'}
                          direction={samOrderBy === 'date' ? samOrder : 'asc'}
                          onClick={() => {
                            const isAsc = samOrderBy === 'date' && samOrder === 'asc';
                            setSamOrder(isAsc ? 'desc' : 'asc');
                            setSamOrderBy('date');
                          }}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={samOrderBy === 'amount'}
                          direction={samOrderBy === 'amount' ? samOrder : 'asc'}
                          onClick={() => {
                            const isAsc = samOrderBy === 'amount' && samOrder === 'asc';
                            setSamOrder(isAsc ? 'desc' : 'asc');
                            setSamOrderBy('amount');
                          }}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={samOrderBy === 'agency'}
                          direction={samOrderBy === 'agency' ? samOrder : 'asc'}
                          onClick={() => {
                            const isAsc = samOrderBy === 'agency' && samOrder === 'asc';
                            setSamOrder(isAsc ? 'desc' : 'asc');
                            setSamOrderBy('agency');
                          }}
                        >
                          Agency
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Set-Aside Status</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...samAwards]
                      .sort(createSortComparator(samOrderBy, samOrder))
                      .slice(samPage * samRowsPerPage, samPage * samRowsPerPage + samRowsPerPage)
                      .map((award, index) => {
                      const hasViolation = hasSetAsideViolation(award.award_id, award.set_aside || '');
                      const violation = getSetAsideViolation(award.award_id, award.set_aside || '');
                      
                      return (
                        <TableRow 
                          key={index} 
                          hover
                          sx={{
                            backgroundColor: hasViolation ? 'error.light' : 'inherit',
                            opacity: hasViolation ? 0.9 : 1,
                            border: hasViolation ? 2 : 0,
                            borderColor: hasViolation ? 'error.main' : 'transparent'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {hasViolation && <Warning color="error" sx={{ fontSize: 16 }} />}
                              {award.award_url ? (
                                <Link href={award.award_url} target="_blank" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {award.award_id} <OpenInNew sx={{ fontSize: 14 }} />
                                </Link>
                              ) : (
                                award.award_id
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{formatDate(award.date)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrency(award.amount)}
                          </TableCell>
                          <TableCell>{award.agency}</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {award.description}
                            {award.naics && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                NAICS: {award.naics}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {award.set_aside ? (
                                <Chip
                                  label={award.set_aside}
                                  color={hasViolation ? 'error' : 'default'}
                                  size="small"
                                  variant={hasViolation ? 'filled' : 'outlined'}
                                  icon={hasViolation ? <Warning /> : undefined}
                                  sx={{ fontWeight: hasViolation ? 'bold' : 'normal' }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                              {violation && (
                                <Alert severity="error" sx={{ py: 0.5, px: 1 }}>
                                  <Typography variant="caption">
                                    Vendor not eligible for {award.set_aside}
                                  </Typography>
                                </Alert>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{award.location_city}, {award.location_state}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={samAwards.length}
                rowsPerPage={samRowsPerPage}
                page={samPage}
                onPageChange={(_, newPage) => setSamPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setSamRowsPerPage(parseInt(event.target.value, 10));
                  setSamPage(0);
                }}
              />
            </>
          ) : (
            <Alert severity="info" icon={<Info />}>
              <Typography variant="subtitle1">No SAM awards found</Typography>
              <Typography variant="body2">
                This vendor does not have any awards in the SAM (System for Award Management) database, 
                or the data may not be available at this time.
              </Typography>
            </Alert>
          )}
        </TabPanel>

        {/* Tab 3: USAspending Awards */}
        <TabPanel value={tabValue} index={2}>
          {usaspendingAwards && usaspendingAwards.length > 0 ? (
            <>
              {usaspendingTotals && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">
                    <strong>USAspending Awards Summary:</strong> {usaspendingTotals.award_count} awards worth {formatCurrency(usaspendingTotals.total_amount)}
                  </Typography>
                  <Typography variant="body2">
                    Average award: {formatCurrency(usaspendingTotals.avg_award_amount || 0)} | 
                    Competition: {usaspendingTotals.full_competition_percentage?.toFixed(0)}% Full, {usaspendingTotals.sole_source_percentage?.toFixed(0)}% Sole Source
                  </Typography>
                </Alert>
              )}
              <TableContainer component={Paper}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Award ID</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={usaOrderBy === 'date'}
                          direction={usaOrderBy === 'date' ? usaOrder : 'asc'}
                          onClick={() => {
                            const isAsc = usaOrderBy === 'date' && usaOrder === 'asc';
                            setUsaOrder(isAsc ? 'desc' : 'asc');
                            setUsaOrderBy('date');
                          }}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={usaOrderBy === 'amount'}
                          direction={usaOrderBy === 'amount' ? usaOrder : 'asc'}
                          onClick={() => {
                            const isAsc = usaOrderBy === 'amount' && usaOrder === 'asc';
                            setUsaOrder(isAsc ? 'desc' : 'asc');
                            setUsaOrderBy('amount');
                          }}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={usaOrderBy === 'agency'}
                          direction={usaOrderBy === 'agency' ? usaOrder : 'asc'}
                          onClick={() => {
                            const isAsc = usaOrderBy === 'agency' && usaOrder === 'asc';
                            setUsaOrder(isAsc ? 'desc' : 'asc');
                            setUsaOrderBy('agency');
                          }}
                        >
                          Agency
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Competition</TableCell>
                      <TableCell>Set-Aside Status</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...usaspendingAwards]
                      .sort(createSortComparator(usaOrderBy, usaOrder))
                      .slice(usaPage * usaRowsPerPage, usaPage * usaRowsPerPage + usaRowsPerPage)
                      .map((award, index) => {
                      const hasViolation = hasSetAsideViolation(award.award_id, award.set_aside || '');
                      const violation = getSetAsideViolation(award.award_id, award.set_aside || '');
                      
                      return (
                        <TableRow 
                          key={index} 
                          hover
                          sx={{
                            backgroundColor: hasViolation ? 'error.light' : 'inherit',
                            opacity: hasViolation ? 0.9 : 1,
                            border: hasViolation ? 2 : 0,
                            borderColor: hasViolation ? 'error.main' : 'transparent'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {hasViolation && <Warning color="error" sx={{ fontSize: 16 }} />}
                              {award.award_url ? (
                                <Link href={award.award_url} target="_blank" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {award.award_id} <OpenInNew sx={{ fontSize: 14 }} />
                                </Link>
                              ) : (
                                award.award_id
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{formatDate(award.date)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrency(award.amount)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {award.agency}
                            </Typography>
                            {award.office && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {award.office}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            {award.description}
                            {award.naics && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                NAICS: {award.naics} - {award.naics_description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {award.competition || 'N/A'}
                            </Typography>
                            {award.number_of_offers && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {award.number_of_offers} offers
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {award.set_aside ? (
                                <Chip
                                  label={award.set_aside}
                                  color={hasViolation ? 'error' : 'default'}
                                  size="small"
                                  variant={hasViolation ? 'filled' : 'outlined'}
                                  icon={hasViolation ? <Warning /> : undefined}
                                  sx={{ fontWeight: hasViolation ? 'bold' : 'normal' }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                              {violation && (
                                <Alert severity="error" sx={{ py: 0.5, px: 1 }}>
                                  <Typography variant="caption">
                                    Vendor not eligible for {award.set_aside}
                                  </Typography>
                                </Alert>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{award.location_city}, {award.location_state}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={usaspendingAwards.length}
                rowsPerPage={usaRowsPerPage}
                page={usaPage}
                onPageChange={(_, newPage) => setUsaPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setUsaRowsPerPage(parseInt(event.target.value, 10));
                  setUsaPage(0);
                }}
              />
            </>
          ) : (
            <Alert severity="info" icon={<Info />}>
              <Typography variant="subtitle1">No USAspending awards found</Typography>
              <Typography variant="body2">
                This vendor does not have any awards in the USAspending database, 
                or the data may not be available at this time.
              </Typography>
            </Alert>
          )}
        </TabPanel>

        {/* Tab 4: Compliance & Risk */}
        <TabPanel value={tabValue} index={3}>
          {selectedVendor && (
            <ComplianceRiskPanel
              vendorUei={selectedVendor.uei_sam}
              vendorName={selectedVendor.legal_business_name}
              legalName={selectedVendor.legal_business_name}
            />
          )}
        </TabPanel>

        {/* Tab 5: Summary & Risk Analysis */}
        <TabPanel value={tabValue} index={4}>
          {vendorSummary ? (
            <Grid container spacing={3}>
              {/* Award Summary */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp /> Award Summary
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" color="primary.main" gutterBottom>
                            {vendorSummary.combined_totals?.award_count || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Awards
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" color="success.main" gutterBottom>
                            {formatCurrency(vendorSummary.combined_totals?.total_amount || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Amount
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" color="info.main" gutterBottom>
                            {formatCurrency(vendorSummary.combined_totals?.avg_award_amount || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Average Award
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Risk Analysis */}
              {(() => {
                const mappedRisk = mapRiskAnalysis(vendorSummary.risk_analysis, vendorSummary.fraud_analysis);
                return mappedRisk ? (
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Security /> Risk Analysis
                        </Typography>
                        
                        <Grid container spacing={3}>
                          {/* Risk Score & Level */}
                          <Grid item xs={12} md={4}>
                            <Alert 
                              severity={
                                mappedRisk.risk_level === 'CRITICAL' ? 'error' :
                                mappedRisk.risk_level === 'HIGH' ? 'warning' :
                                mappedRisk.risk_level === 'MEDIUM' ? 'info' : 'success'
                              }
                            >
                              <AlertTitle>Risk Assessment</AlertTitle>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <RiskScoreBadge 
                                  score={mappedRisk.risk_score} 
                                  level={mappedRisk.risk_level}
                                  size="medium"
                                  variant="filled"
                                />
                              </Box>
                              <Typography variant="body2">
                                Investigation Priority: <strong>{mappedRisk.investigation_priority}</strong>
                              </Typography>
                              <Typography variant="body2">
                                Fraud Probability: {(mappedRisk.fraud_probability * 100).toFixed(1)}%
                              </Typography>
                            </Alert>
                          </Grid>

                          {/* Risk Indicators */}
                          <Grid item xs={12} md={8}>
                            {mappedRisk.risk_indicators.length > 0 ? (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Risk Indicators ({mappedRisk.risk_indicators.length}):
                                </Typography>
                                <FraudIndicatorChips 
                                  indicators={mappedRisk.risk_indicators}
                                  maxDisplay={8}
                                  showDetails={true}
                                />
                              </Box>
                            ) : (
                              <Alert severity="success" variant="outlined">
                                <Typography variant="body2">
                                  No fraud indicators detected for this vendor.
                                </Typography>
                              </Alert>
                            )}
                          </Grid>
                        </Grid>

                        {mappedRisk.requires_investigation && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            <AlertTitle>⚠️ Investigation Required</AlertTitle>
                            This vendor requires immediate investigation based on detected fraud indicators.
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ) : null;
              })()}

              {/* Competition Analysis */}
              {(() => {
                const mappedCompetition = mapCompetitionAnalysis(vendorSummary.combined_totals);
                return mappedCompetition ? (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Assessment /> Competition Analysis
                        </Typography>
                        
                        <CompetitionHealthIndicator
                          competitionRate={mappedCompetition.competition_rate}
                          awardsWithData={mappedCompetition.awards_with_data}
                          totalAwards={mappedCompetition.total_awards}
                          fullCompetitionCount={mappedCompetition.full_competition_count}
                          soleSourceCount={mappedCompetition.sole_source_count}
                          variant="detailed"
                          showDetails={true}
                        />

                        <Divider sx={{ my: 2 }} />

                        <List dense>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemText
                              primary="Competition Summary"
                              secondary={`${mappedCompetition.full_competition_percentage.toFixed(0)}% Full Competition, ${mappedCompetition.sole_source_percentage.toFixed(0)}% Sole Source`}
                            />
                          </ListItem>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemText
                              primary="Data Availability"
                              secondary={`${mappedCompetition.awards_with_data} of ${mappedCompetition.total_awards} awards have competition data`}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ) : null;
              })()}

              {/* Fraud Analysis */}
              {vendorSummary.fraud_analysis && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assessment /> Fraud Analysis Summary
                      </Typography>
                      
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {vendorSummary.fraud_analysis.summary}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Total Indicators</Typography>
                          <Typography variant="h4" color="primary.main">
                            {vendorSummary.fraud_analysis.total_indicators}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Risk Level</Typography>
                          <Typography variant="h6" color={
                            vendorSummary.fraud_analysis.risk_level === 'HIGH' ? 'error.main' : 
                            vendorSummary.fraud_analysis.risk_level === 'MEDIUM' ? 'warning.main' : 'success.main'
                          }>
                            {vendorSummary.fraud_analysis.risk_level}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Indicators by Severity:</Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={3}>
                            <Chip 
                              label={`${vendorSummary.fraud_analysis.indicators_by_severity?.CRITICAL || 0} Critical`}
                              color="error"
                              size="small"
                              variant={(vendorSummary.fraud_analysis.indicators_by_severity?.CRITICAL || 0) > 0 ? "filled" : "outlined"}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Chip 
                              label={`${vendorSummary.fraud_analysis.indicators_by_severity?.HIGH || 0} High`}
                              color="warning"
                              size="small"
                              variant={(vendorSummary.fraud_analysis.indicators_by_severity?.HIGH || 0) > 0 ? "filled" : "outlined"}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Chip 
                              label={`${vendorSummary.fraud_analysis.indicators_by_severity?.MEDIUM || 0} Medium`}
                              color="info"
                              size="small"
                              variant={(vendorSummary.fraud_analysis.indicators_by_severity?.MEDIUM || 0) > 0 ? "filled" : "outlined"}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Chip 
                              label={`${vendorSummary.fraud_analysis.indicators_by_severity?.LOW || 0} Low`}
                              color="default"
                              size="small"
                              variant={(vendorSummary.fraud_analysis.indicators_by_severity?.LOW || 0) > 0 ? "filled" : "outlined"}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Agency Diversity */}
              {vendorSummary.agency_diversity && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PieChart /> Agency Diversity
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="h4" color="primary.main" gutterBottom>
                            {vendorSummary.agency_diversity.unique_agencies}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Unique Agencies
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                          <Typography variant="subtitle2" gutterBottom>
                            Top Agencies:
                          </Typography>
                          <List dense>
                            {vendorSummary.agency_diversity.top_agencies?.slice(0, 5).map((agency: any, index: number) => (
                              <ListItem key={index} sx={{ px: 0 }}>
                                <ListItemText
                                  primary={agency.name}
                                  secondary={`${agency.count} awards (${agency.percentage?.toFixed(0)}%)`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          ) : (
            loading ? <LoadingSkeleton /> : (
              <Alert severity="info">
                <Typography variant="subtitle1">Summary not available</Typography>
                <Typography variant="body2">
                  Summary and risk analysis data is not available for this vendor.
                </Typography>
              </Alert>
            )
          )}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" size="large">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorDetailModal;